import React, { useState, useMemo } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import chartConfig from './chartConfig.json';

// Defines the structure of chart data
interface ChartData {
  name: string;
  opened: number;
  clicked: number;
}

interface ChartComponentProps {
  data?: {
    '24h'?: ChartData[];
    '7d'?: ChartData[];
    '30d'?: ChartData[];
  };
}

// Renders a bar chart displaying engagement metrics (opened and clicked) for emails
const ChartComponent: React.FC<ChartComponentProps> = ({ data }) => {
  const [filter, setFilter] = useState<'24h' | '7d' | '30d'>('24h'); // Tracks the selected time range filter

  // Default data for different time ranges
  const defaultData = {
    '24h': [
      { name: 'Welcome Email', opened: 1200, clicked: 300 },
      { name: 'Product Introduction Email', opened: 950, clicked: 220 },
      { name: 'Final Email', opened: 700, clicked: 150 },
    ],
    '7d': [
      { name: 'Welcome Email', opened: 4200, clicked: 900 },
      { name: 'Product Introduction Email', opened: 3600, clicked: 750 },
      { name: 'Final Email', opened: 2800, clicked: 600 },
    ],
    '30d': [
      { name: 'Welcome Email', opened: 12000, clicked: 2500 },
      { name: 'Product Introduction Email', opened: 9500, clicked: 1800 },
      { name: 'Special Offer Email', opened: 7000, clicked: 1400 },
      { name: 'Final Reminder Email', opened: 5000, clicked: 1000 },
    ],
  };

  // Selects data based on the current filter, falling back to default data if none provided
  const selectedData = useMemo(() => {
    return data && data[filter] ? data[filter]! : defaultData[filter];
  }, [data, filter]);

  // Loads fallback data from chartConfig.json
  const fallbackData = useMemo(
    () => ({
      labels: chartConfig.data.labels,
      opened: chartConfig.data.datasets[0].data,
      clicked: chartConfig.data.datasets[1].data,
    }),
    []
  );

  // Prepares series data for ApexCharts
  const series = useMemo(
    () => [
      {
        name: 'Opened',
        data: selectedData.length
          ? selectedData.map((item) => item.opened)
          : fallbackData.opened,
      },
      {
        name: 'Clicked',
        data: selectedData.length
          ? selectedData.map((item) => item.clicked)
          : fallbackData.clicked,
      },
    ],
    [selectedData, fallbackData]
  );

  // Configures ApexCharts options for rendering the bar chart
  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        toolbar: { show: false },
      },
      xaxis: {
        categories: selectedData.length
          ? selectedData.map((item) => item.name)
          : fallbackData.labels,
        labels: { style: { fontSize: '12px', colors: '#657584' } },
      },
      yaxis: {
        labels: { style: { fontSize: '12px', colors: '#657584' } },
        min: chartConfig.options.scales.y.beginAtZero ? 0 : undefined,
        title: {
          text: 'Count',
          style: { fontSize: '12px', color: '#657584' },
        },
      },
      colors: [
        chartConfig.data.datasets[0].backgroundColor,
        chartConfig.data.datasets[1].backgroundColor,
      ],
      legend: {
        position: chartConfig.options.plugins.legend.position as
          | 'top'
          | 'right'
          | 'bottom'
          | 'left'
          | undefined,
        horizontalAlign: 'left',
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '30%',
          borderRadius: 4,
        },
      },
      grid: { borderColor: '#fff' },
      dataLabels: { enabled: false },
      tooltip: {
        y: {
          formatter: (val) => `${val.toLocaleString()}`, // Formats tooltip values with thousands separator
        },
      },
    }),
    [selectedData, fallbackData]
  );

  return (
    <div className='bg-white p-4 rounded-md shadow-sm border h-full'>
      <div className='flex justify-between items-center relative'>
        <h3 className='text-[16px] font-semibold text-[#3F5265]'>
          Engagement Metrics
        </h3>
        <select
          className='border rounded p-2 text-[14px] border-[#E1EAF0] bg-[#F5F8FA] text-[#3F5265] absolute z-10 top-0 right-0'
          value={filter}
          onChange={(e) => setFilter(e.target.value as '24h' | '7d' | '30d')} // Updates the time range filter
        >
          <option value='24h'>Last 24 Hours</option>
          <option value='7d'>Last 7 Days</option>
          <option value='30d'>Last 30 Days</option>
        </select>
      </div>
      <Chart options={options} series={series} type='bar' height={300} />
    </div>
  );
};

export default ChartComponent;
